/**
 * @module app.shared
 */

export class StyleSizeValueConverter {

    /**
     * Applies a CSS Size Unit to a value
     *
     * * If `value` is a `number` or a `string` that can converted to a `number`, then append `defaultUnit`
     * * If `value` is a `string` that cannot be converted to a `number`, return `value` as-is
     * * If `value` is `true`, return `trueValue` and optionally append `defaultUnit` (using the same algorithm used for `value`)
     * * If `value` is `false`, return `falseValue` and optionally append `defaultUnit` (using the same algorithm used for `value`)
     * * Otherwise, return `defaultValue` and optionally append `defaultUnit` (using the same algorithm used for `value`)
     *
     * @param value the size value as a `number` or `string`, with or without a unit, or `true` or `false`.
     * @param defaultUnit (*optional*, default `'px`') the default unit to apply
     * @param trueValue (*optional*, default `''`) the result to return if `value` is `true`
     * @param falseValue (*optional*, default `''`) the result to return if `value` is `false`
     * @param defaultValue (*optional*, default `''`) the result to return if `value` is `null` or `undefined`
     */
    public toView(value: unknown, defaultUnit: unknown = 'px', trueValue: unknown = '', falseValue: unknown = '', defaultValue: unknown = ''): string {
        const valueWithUnit = this.withDefaultUnit(value, defaultUnit);
        if(valueWithUnit) { return valueWithUnit; }

        const fallback =
            value === true ? trueValue :
            value === false ? falseValue :
            defaultValue;

        const fallbackWithUnit = this.withDefaultUnit(fallback, defaultUnit);
        if(fallbackWithUnit) { return fallbackWithUnit; }

        return '';
    }

    private withDefaultUnit(value: unknown, defaultUnit: unknown): string {
        if(value === undefined || value === null) { return ''; }

        const unit = `${defaultUnit || ''}`;
        if(typeof value === 'boolean') {
            return '';
        }
        else if(typeof value === 'number') {
            return `${value}${unit}`;
        }
        else if(typeof value === 'string') {
            const number = parseFloat(value);
            if(isNaN(number)) { return value; }
            return `${value}${unit}`;
        }

        return `${value}`;
    }

}
